import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Category as SemiIcon,
  CheckCircleOutline as OkIcon,
  Checkroom as CoverIcon,
  Close as CloseIcon,
  HelpOutline as HelpIcon,
  Refresh as RefreshIcon,
  WarehouseOutlined as StoresIcon,
  Search as SearchIcon,
  Texture as FabricIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import { MP_LABEL, storeOptions } from '../../components/products/msAssortmentApi.mjs';

const ROLES = {
  cover: { label: 'Чехол', Icon: CoverIcon, color: 'primary' },
  fabric: { label: 'Ткань', Icon: FabricIcon, color: 'secondary' },
  semi: { label: 'Полуфабрикат', Icon: SemiIcon, color: 'info' },
};

const FILTERS = [
  { value: 'all', label: 'Все' },
  { value: 'attention', label: 'Требуют внимания' },
  { value: 'extra', label: 'С добавкой' },
  { value: 'nochain', label: 'Без цепочки' },
];

function fmt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function unitOf(role) {
  return role === 'fabric' ? 'м' : 'шт.';
}

function accountLabel(a) {
  return `${MP_LABEL[a.marketplace] || a.marketplace} · ${a.account_name}`;
}

function flattenTree(nodes, depth = 0, out = []) {
  for (const node of nodes) {
    out.push({ node, depth });
    flattenTree(node.children || [], depth + 1, out);
  }
  return out;
}

function needsAttention(item) {
  return item.issues.length > 0 || Boolean(item.pair && !item.pair.in_chain);
}

function matchesFilter(item, filter) {
  if (filter === 'attention') return needsAttention(item);
  if (filter === 'extra') return item.extra > 0;
  if (filter === 'nochain') return item.tree.length === 0;
  return true;
}

function planNameOf(item) {
  const name = item.cards?.find((c) => c.id === item.plan_id)?.name;
  if (name) return name;
  return item.cards?.length ? 'Техкарта не выбрана' : 'Без техкарты';
}

function StatusIcon({ item }) {
  if (needsAttention(item)) {
    return <Tooltip title="Требует внимания"><WarningIcon color="warning" /></Tooltip>;
  }
  return <Tooltip title="Всё в порядке"><OkIcon color="success" /></Tooltip>;
}

function TechcardSelect({ label, msId, planId, planReason, cards, saving, onChange }) {
  if (!cards?.length) return null;
  const value = planId && cards.some((c) => c.id === planId) ? planId : '';
  const needsChoice = !value && cards.length > 1;
  let helper = ' ';
  if (needsChoice) helper = `Выберите одну из ${cards.length}`;
  else if (planReason === 'single' && value) helper = 'Единственная — выбрана автоматически';
  return (
    <FormControl size="small" fullWidth error={needsChoice} disabled={saving}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(msId, e.target.value || null)}>
        <MenuItem value=""><em>Не выбрана</em></MenuItem>
        {cards.map((c) => (
          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
        ))}
      </Select>
      <FormHelperText>{helper}</FormHelperText>
    </FormControl>
  );
}

function IssueAlerts({ item }) {
  const ambiguous = item.issues.filter((i) => i.kind === 'ambiguous').length;
  const unresolved = item.issues.length - ambiguous;
  const pairMismatch = item.pair && !item.pair.in_chain;
  if (!ambiguous && !unresolved && !pairMismatch) return null;
  return (
    <Stack spacing={1}>
      {ambiguous > 0 && (
        <Alert severity="warning">
          {ambiguous > 1 ? `Нужно выбрать техкарты: ${ambiguous}` : 'Нужно выбрать техкарту'} — без выбора материал не учитывается.
        </Alert>
      )}
      {unresolved > 0 && (
        <Alert severity="error">Параметрика техкарты не разрешилась: {unresolved}.</Alert>
      )}
      {pairMismatch && (
        <Alert severity="error">Пара для производства не совпадает ни с одним чехлом из техкарты.</Alert>
      )}
    </Stack>
  );
}

function CalcTable({ item }) {
  const rows = [
    ['Свой остаток', fmt(item.own)],
    ['Соберём по техкартам', item.extra > 0 ? `+${fmt(item.extra)}` : '—'],
  ];
  return (
    <Table size="small">
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell>{label}</TableCell>
            <TableCell align="right">{value}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell><Typography variant="subtitle2">Итого для МП</Typography></TableCell>
          <TableCell align="right"><Typography variant="subtitle2">{fmt(item.total)}</Typography></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function ChainTable({ rows }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Материал</TableCell>
            <TableCell align="right">Норма</TableCell>
            <TableCell align="right">Остаток</TableCell>
            <TableCell align="right">Соберём</TableCell>
            <TableCell align="right">Хватит на</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ node, depth }, idx) => {
            const role = ROLES[node.role] || ROLES.semi;
            return (
              <TableRow key={`${idx}-${node.id}`}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: depth * 3 }}>
                    <Tooltip title={role.label}>
                      <role.Icon fontSize="small" color={role.color} />
                    </Tooltip>
                    <Box>
                      <Typography variant="body2">{node.name}</Typography>
                      {!node.counted && (
                        <Typography variant="caption" color="warning.main" display="block">
                          Не учитывается: выберите техкарту
                        </Typography>
                      )}
                      {node.unresolved > 0 && (
                        <Typography variant="caption" color="error" display="block">
                          Параметрика не разрешилась: {node.unresolved}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="right">{fmt(node.qty)} {unitOf(node.role)}</TableCell>
                <TableCell align="right">
                  {node.counted ? `${fmt(node.stock)} ${unitOf(node.role)}` : '—'}
                </TableCell>
                <TableCell align="right">
                  {node.counted && node.make > 0 ? `+${fmt(node.make)}` : '—'}
                </TableCell>
                <TableCell align="right">
                  {!node.counted && '—'}
                  {node.counted && node.limiting && (
                    <Tooltip title="Узкое место: этот материал ограничивает сборку">
                      <Chip size="small" color="warning" label={`${fmt(node.units_for_parent)} шт.`} />
                    </Tooltip>
                  )}
                  {node.counted && !node.limiting && `${fmt(node.units_for_parent)} шт.`}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ItemDetails({ item, savingId, onPlanChange }) {
  const rows = flattenTree(item.tree);
  const materials = [...new Map(
    rows
      .filter(({ node }) => node.role !== 'fabric' && node.cards?.length)
      .map(({ node }) => [node.id, node]),
  ).values()];

  return (
    <Stack spacing={3}>
      <IssueAlerts item={item} />

      <Box>
        <Typography variant="subtitle1" gutterBottom>Расчёт остатка</Typography>
        <CalcTable item={item} />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Свой остаток — по складам товара всех связок. Если в кабинете задан принуд, на МП уходит он.
        </Typography>
      </Box>

      {item.accounts?.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>Связан с кабинетами</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {item.accounts.map((a) => (
              <Chip key={`${a.marketplace}-${a.account_name}`} size="small" variant="outlined" label={accountLabel(a)} />
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle1" gutterBottom>Техкарта товара</Typography>
        {item.cards?.length ? (
          <TechcardSelect
            label="Техкарта"
            msId={item.ms_id}
            planId={item.plan_id}
            planReason={item.plan_reason}
            cards={item.cards}
            saving={savingId === item.ms_id}
            onChange={onPlanChange}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">У товара нет техкарты.</Typography>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>Пара для производства</Typography>
        {item.pair ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">{item.pair.name || item.pair.id}</Typography>
            <Chip
              size="small"
              variant="outlined"
              color={item.pair.in_chain ? 'success' : 'error'}
              label={item.pair.in_chain ? 'совпадает с техкартой' : 'нет в техкарте'}
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">Не задана.</Typography>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>Цепочка материалов</Typography>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            В техкарте нет чехла, ткани или полуфабриката с ними — на МП уходит только свой остаток.
          </Typography>
        ) : (
          <ChainTable rows={rows} />
        )}
      </Box>

      {materials.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>Техкарты материалов</Typography>
          <Stack spacing={1}>
            {materials.map((node) => (
              <TechcardSelect
                key={node.id}
                label={node.name}
                msId={node.id}
                planId={node.plan_id}
                planReason={node.plan_reason}
                cards={node.cards}
                saving={savingId === node.id}
                onChange={onPlanChange}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

function HelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Как считается остаток на МП</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            На МП уходит свой остаток товара плюс сколько ещё можно собрать: из чехлов, полуфабрикатов (крой, пуф
            с поролоном) и ткани по нормам из техкарт. Если материалов несколько, итог ограничивает «узкое место».
          </Typography>
          <Typography variant="body2">
            Настройка одна на товар МС для всех кабинетов: выбор техкарты общий с «Товары МС» и действует
            в автопополнении и производстве. Склады чехлов и ткани задаются кнопкой «Склады» один раз для всех связок.
          </Typography>
          <Typography variant="body2">
            При отправке на МП свой остаток берётся со складов МС конкретной связки, а принуд кабинета перекрывает расчёт.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Понятно</Button>
      </DialogActions>
    </Dialog>
  );
}

function StoresDialog({ open, onClose, onSaved }) {
  const { error: showError, success: showSuccess } = useNotify();
  const [stores, setStores] = useState([]);
  const [productIds, setProductIds] = useState([]);
  const [semiIds, setSemiIds] = useState([]);
  const [fabricIds, setFabricIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get('/tech-chain/settings'),
      axios.get('/mswb/ms-stores', { silent: true }).catch(() => ({ data: [] })),
    ])
      .then(([{ data }, storesRes]) => {
        setStores(storeOptions(storesRes.data));
        setProductIds(data?.product_store_ids || []);
        setSemiIds(data?.settings?.semi_store_ids || []);
        setFabricIds(data?.settings?.fabric_store_ids || []);
      })
      .catch((e) => showError(e?.response?.data?.message || 'Не удалось загрузить склады'))
      .finally(() => setLoading(false));
  }, [open, showError]);

  const nameOf = (id) => stores.find((s) => s.id === id)?.name || id;
  const picker = (label, value, onChange) => (
    <Autocomplete
      multiple
      options={stores}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={value.map((id) => stores.find((s) => s.id === id) || { id, name: id })}
      onChange={(_, val) => onChange(val.map((v) => v.id))}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText="Пусто — те же, что для товара" />
      )}
    />
  );

  const save = async () => {
    setSaving(true);
    try {
      await axios.put('/tech-chain/settings', { semi_store_ids: semiIds, fabric_store_ids: fabricIds });
      showSuccess('Склады сохранены');
      onClose();
      onSaved();
    } catch (e) {
      showError(e?.response?.data?.message || 'Не удалось сохранить склады');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Склады МС для расчёта</DialogTitle>
      <DialogContent>
        {loading ? <LinearProgress /> : (
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Склады товара</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {productIds.length
                  ? productIds.map((id) => <Chip key={id} size="small" label={nameOf(id)} />)
                  : <Typography variant="body2" color="text.secondary">Не выбраны ни в одной связке</Typography>}
              </Stack>
              <FormHelperText>
                Задаются в каждой связке («Товары МС» → «Склады» → «Связь с МП»); на этой странице — все вместе.
              </FormHelperText>
            </Box>
            {picker('Склады чехлов и полуфабрикатов', semiIds, setSemiIds)}
            {picker('Склады ткани', fabricIds, setFabricIds)}
            <Alert severity="info">Эти склады общие для всех кабинетов и связок, в том числе при отправке остатков на МП.</Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={loading || saving}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TechChainCheckMui() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { success: showSuccess, error: showError } = useNotify();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [storesOpen, setStoresOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/tech-chain/items');
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setItems([]);
      showError(e?.response?.data?.message || 'Не удалось посчитать остатки');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const handlePlanChange = async (msId, planId) => {
    setSavingId(msId);
    try {
      await axios.put('/tech-chain/plan', { ms_id: msId, processing_plan_id: planId });
      showSuccess(planId ? 'Техкарта выбрана' : 'Выбор техкарты снят');
      await load();
    } catch (e) {
      showError(e?.response?.data?.message || 'Не удалось сохранить техкарту');
    } finally {
      setSavingId(null);
    }
  };

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.value, items.filter((it) => matchesFilter(it, f.value)).length]),
  ), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (!matchesFilter(it, filter)) return false;
      if (!q) return true;
      return String(it.name).toLowerCase().includes(q) || it.product_keys.some((k) => k.includes(q));
    });
  }, [items, query, filter]);

  const selected = filtered.find((it) => it.ms_id === selectedId) || (isMobile ? null : filtered[0]) || null;

  const details = selected && (
    <ItemDetails item={selected} savingId={savingId} onPlanChange={handlePlanChange} />
  );

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Товар или артикул МП"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                },
              }}
            />
            <Stack direction="row" spacing={1}>
              <Button startIcon={<StoresIcon />} onClick={() => setStoresOpen(true)}>
                Склады
              </Button>
              <Button startIcon={<HelpIcon />} onClick={() => setHelpOpen(true)}>
                Как считается
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={load}
                disabled={loading}
              >
                Пересчитать
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined">
            <Tabs
              value={filter}
              onChange={(_, v) => setFilter(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {FILTERS.map((f) => (
                <Tab key={f.value} value={f.value} label={`${f.label} · ${counts[f.value]}`} />
              ))}
            </Tabs>
            <Divider />
            {loading && <LinearProgress />}
            {!loading && filtered.length === 0 ? (
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center">
                  Ничего не найдено
                </Typography>
              </CardContent>
            ) : (
              <List disablePadding sx={{ maxHeight: { md: '70vh' }, overflow: 'auto' }}>
                {filtered.map((item) => (
                  <ListItemButton
                    key={item.ms_id}
                    divider
                    selected={selected?.ms_id === item.ms_id}
                    onClick={() => setSelectedId(item.ms_id)}
                  >
                    <ListItemIcon><StatusIcon item={item} /></ListItemIcon>
                    <ListItemText primary={item.name} secondary={planNameOf(item)} />
                    <Box sx={{ ml: 2, textAlign: 'right' }}>
                      <Typography variant="subtitle2">{fmt(item.total)}</Typography>
                      <Typography variant="caption" color="text.secondary">итого</Typography>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Card>
        </Grid>

        {!isMobile && (
          <Grid size={{ xs: 12, md: 7 }}>
            <Card variant="outlined">
              {selected ? (
                <>
                  <CardHeader
                    title={selected.name}
                    subheader={planNameOf(selected)}
                    action={<StatusIcon item={selected} />}
                    slotProps={{ title: { variant: 'h6' } }}
                  />
                  <Divider />
                  <CardContent>{details}</CardContent>
                </>
              ) : (
                <CardContent>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Выберите товар в списке
                  </Typography>
                </CardContent>
              )}
            </Card>
          </Grid>
        )}
      </Grid>

      {isMobile && (
        <Dialog fullScreen open={Boolean(selected)} onClose={() => setSelectedId(null)}>
          {selected && (
            <>
              <DialogTitle sx={{ pr: 7 }}>
                {selected.name}
                <IconButton
                  aria-label="Закрыть"
                  onClick={() => setSelectedId(null)}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>{details}</DialogContent>
            </>
          )}
        </Dialog>
      )}

      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <StoresDialog open={storesOpen} onClose={() => setStoresOpen(false)} onSaved={load} />
    </Stack>
  );
}
